/**
 * Digitalsprung custom JavaScript functions
 * For Shopify theme customizations
 */

/**
 * Handles quantity input fields to enforce step constraints (Mengenstaffelung)
 * Automatically rounds input values to the nearest valid step value
 */
class DigitalsprungQuantityHandler {
  constructor() {
    // Bind methods to maintain 'this' context
    this.handleQuantityChange = this.handleQuantityChange.bind(this);
    this.handleQuantityBlur = this.handleQuantityBlur.bind(this);
    this.setupQuantityInputs = this.setupQuantityInputs.bind(this);
    
    this.init();
  }

  init() {
    // Initialize event listeners for all quantity inputs
    this.setupQuantityInputs();
    
    // Re-initialize when the document content changes (for dynamically added elements)
    document.addEventListener('page:loaded', this.setupQuantityInputs);
    document.addEventListener('shopify:section:load', this.setupQuantityInputs);
  }

  setupQuantityInputs() {
    // Find all quantity inputs
    const quantityInputs = document.querySelectorAll('.quantity__input');
    
    quantityInputs.forEach(input => {
      // Remove existing event listeners to prevent duplicates
      input.removeEventListener('change', this.handleQuantityChange);
      input.removeEventListener('blur', this.handleQuantityBlur);
      
      // Add event listeners
      input.addEventListener('change', this.handleQuantityChange);
      input.addEventListener('blur', this.handleQuantityBlur);
      
      // Remove readonly attribute to allow editing
      input.removeAttribute('readonly');
    });
  }

  handleQuantityChange(event) {
    const input = event.target;
    
    // Get step value from data attribute or default to 1
    const step = parseFloat(input.dataset.step || input.getAttribute('step') || 1);
    const min = parseFloat(input.dataset.min || input.getAttribute('min') || 1);
    const max = parseFloat(input.dataset.max || input.getAttribute('max') || Infinity);
    
    // Get current value
    let value = parseFloat(input.value);
    
    // Round to nearest step
    const remainder = (value - min) % step;
    
    if (remainder !== 0) {
      // Round to nearest step
      if (remainder >= step / 2) {
        // Round up
        value = Math.min(value - remainder + step, max);
      } else {
        // Round down
        value = Math.max(value - remainder, min);
      }
      
      // Update input value
      input.value = value;
    }
    
    // Ensure value is within min/max bounds
    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  handleQuantityBlur(event) {
    // Same as handleQuantityChange but ensures validation happens on blur
    this.handleQuantityChange(event);
  }
}

// Initialize theme object with localization strings if needed
if (!window.theme) {
  window.theme = {};
}

if (!window.theme.strings) {
  window.theme.strings = {
    general: {
      cart: {
        mengenstaffelung: {
          popup_title: "Bestellmenge nicht möglich",
          close_button_label: "Schließen",
          explanation: "Die folgenden Produkte haben eine Mengenstaffelung, die nicht eingehalten wurde:",
          current_quantity: "Aktuelle Menge: {{ quantity }}",
          step_size: "Mengenstaffelung: {{ step }}",
          suggested_quantity: "Empfohlene Menge: {{ quantity }}",
          back_to_cart: "Zurück zum Warenkorb",
          proceed_anyway: "Trotzdem fortfahren",
          unknown_product: "Unbekanntes Produkt"
        }
      }
    }
  };
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  new DigitalsprungQuantityHandler();
  new CartMengenstaffelungValidator();
});

/**
 * Validates cart items to ensure they respect the mengenstaffelung (quantity step) requirements
 * Shows a popup with products that have incorrect quantities when checkout button is clicked
 */
class CartMengenstaffelungValidator {
  constructor() {
    this.init();
  }

  init() {
    // Bind methods to maintain 'this' context
    this.handleCheckoutClick = this.handleCheckoutClick.bind(this);
    this.validateCartItems = this.validateCartItems.bind(this);
    this.showInvalidItemsPopup = this.showInvalidItemsPopup.bind(this);
    this.setupCheckoutButtons = this.setupCheckoutButtons.bind(this);
    
    // Find all checkout buttons
    this.setupCheckoutButtons();
    
    // Re-initialize when the document content changes (for dynamically added elements)
    document.addEventListener('page:loaded', this.setupCheckoutButtons);
    document.addEventListener('shopify:section:load', this.setupCheckoutButtons);
    
    // Also check when cart is updated
    document.addEventListener('cart:updated', this.setupCheckoutButtons);
    
    console.log('CartMengenstaffelungValidator initialized');
  }

  setupCheckoutButtons() {
    // Find all checkout buttons - more specific selectors to ensure we catch all checkout buttons
    const checkoutButtons = document.querySelectorAll('[name="checkout"], [href="/checkout"], form[action="/checkout"] [type="submit"], form#cart [name="checkout"], .btn--primary[name="checkout"]');
  
    console.log('Found checkout buttons:', checkoutButtons.length);
  
    checkoutButtons.forEach(button => {
      // Remove existing event listeners to prevent duplicates
      button.removeEventListener('click', this.handleCheckoutClick);
      
      // Add event listener
      button.addEventListener('click', this.handleCheckoutClick.bind(this));
      console.log('Added click handler to checkout button:', button);
    });
    
    // Handle PayPal buttons
    this.setupPayPalButtons();
  }
  
  setupPayPalButtons() {
    // Initial setup for PayPal buttons that might be already in the DOM
    this.attachPayPalListeners();
    
    // Use MutationObserver to detect when PayPal buttons are added to the DOM
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes && mutation.addedNodes.length > 0) {
          for (let i = 0; i < mutation.addedNodes.length; i++) {
            const node = mutation.addedNodes[i];
            if (node.nodeName === 'SHOPIFY-PAYPAL-BUTTON') {
              console.log('PayPal button detected:', node);
              this.attachPayPalListeners();
            }
          }
        }
      });
    });
    
    // Start observing the document with the configured parameters
    observer.observe(document.body, { childList: true, subtree: true });
  }
  
  attachPayPalListeners() {
    const paypalButtons = document.querySelectorAll('shopify-paypal-button');
    console.log('Found PayPal buttons:', paypalButtons.length);
    
    paypalButtons.forEach(button => {
      // We can't directly attach to the PayPal button's click event
      // Instead, we'll intercept it at the capture phase on the document level
      if (!button.dataset.mengenstaffelungHandled) {
        button.dataset.mengenstaffelungHandled = 'true';
        
        // Add a click event listener to the button container
        button.addEventListener('click', (event) => {
          console.log('PayPal button clicked, validating cart items...');
          try {
            // Check if all cart items respect mengenstaffelung
            const invalidItems = this.validateCartItems();
            
            if (invalidItems.length > 0) {
              console.log('Found invalid items for PayPal checkout:', invalidItems);
              event.preventDefault();
              event.stopPropagation();
              
              // Show popup with invalid items
              this.showInvalidItemsPopup(invalidItems);
              return false;
            }
            
            console.log('All items are valid for PayPal checkout');
            // If all items are valid, allow checkout to proceed
            return true;
          } catch (error) {
            // If there's an error in validation, log it but allow the checkout to proceed
            console.error('Error validating cart for PayPal checkout:', error);
            return true;
          }
        }, true); // Use capture phase
        
        console.log('Added validation handler to PayPal button');
      }
    });
  }

  handleCheckoutClick(event) {
    console.log('Checkout button clicked, validating cart items...');
    try {
      // Check if all cart items respect mengenstaffelung
      const invalidItems = this.validateCartItems();
      
      if (invalidItems.length > 0) {
        console.log('Found invalid items:', invalidItems);
        event.preventDefault();
        event.stopPropagation();
        
        // Show popup with invalid items
        this.showInvalidItemsPopup(invalidItems);
        return false;
      }
      
      console.log('All items are valid, proceeding to checkout');
      // If all items are valid, allow checkout to proceed
      return true;
    } catch (error) {
      // If there's an error in validation, log it but allow the checkout to proceed
      console.error('Error validating cart items:', error);
      return true;
    }
  }

  validateCartItems() {
    const invalidItems = [];
    // Target both cart items in the main cart and potentially in the cart drawer
    const cartItems = document.querySelectorAll('.cart-item, tr[id^="CartItem-"]');
  
    console.log('Validating cart items:', cartItems.length);
  
    cartItems.forEach(item => {
      const quantityInput = item.querySelector('.quantity__input');
      if (!quantityInput) {
        console.log('No quantity input found for item');
        return;
      }
      
      // Get product title from various possible selectors
      let productTitle = '';
      const titleElement = 
        item.querySelector('.cart-item__title') || 
        item.querySelector('.cart-item__name') || 
        item.querySelector('a[class*="cart-item__name"]');
      
      if (titleElement) {
        productTitle = titleElement.textContent.trim();
      } else {
        // Try to find any heading or link that might contain the product title
        const possibleTitleElement = 
          item.querySelector('h3') || 
          item.querySelector('a[href*="/products/"]');
        
        productTitle = possibleTitleElement ? 
          possibleTitleElement.textContent.trim() : window.theme.strings?.general?.cart?.mengenstaffelung?.unknown_product || 'Unbekanntes Produkt';
      }
      
      const quantity = parseFloat(quantityInput.value);
      const step = parseFloat(quantityInput.dataset.step || quantityInput.getAttribute('step') || 1);
      const min = parseFloat(quantityInput.dataset.min || quantityInput.getAttribute('min') || 1);
      
      console.log(`Checking item: "${productTitle}", quantity: ${quantity}, step: ${step}, min: ${min}`);
      
      // Skip validation if no special step is defined (standard increment of 1)
      if (step <= 1) {
        console.log('Skipping validation - no special step defined');
        return;
      }
      
      // Check if quantity is a valid multiple of step starting from min
      const remainder = (quantity - min) % step;
      if (Math.abs(remainder) > 0.001) { // Using small epsilon for floating point comparison
        console.log(`Invalid quantity found: ${quantity} is not a valid step of ${step} from minimum ${min}`);
        console.log(`Remainder: ${remainder}`);
        
        // Calculate the nearest valid quantity
        const suggestedQuantity = Math.round((quantity - min) / step) * step + min;
        
        invalidItems.push({
          title: productTitle,
          quantity: quantity,
          step: step,
          min: min,
          suggestedQuantity: suggestedQuantity
        });
      }
    });
  
    return invalidItems;
  }

  showInvalidItemsPopup(invalidItems) {
    console.log('Showing popup for invalid items:', invalidItems);
  
    // Create popup if it doesn't exist
    let popup = document.getElementById('mengenstaffelung-popup');
    if (!popup) {
      popup = document.createElement('div');
      popup.id = 'mengenstaffelung-popup';
      popup.className = 'mengenstaffelung-popup';
      document.body.appendChild(popup);
      
      // Add styles if they don't exist
      if (!document.getElementById('mengenstaffelung-popup-styles')) {
        const style = document.createElement('style');
        style.id = 'mengenstaffelung-popup-styles';
        style.textContent = `
          .mengenstaffelung-popup {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: white;
            padding: 20px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            z-index: 1000;
            max-width: 90%;
            width: 500px;
            max-height: 90vh;
            overflow-y: auto;
          }
          .mengenstaffelung-popup-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 999;
          }
          .mengenstaffelung-popup-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 1px solid #eee;
          }
          .mengenstaffelung-popup-title {
            font-size: 18px;
            font-weight: bold;
            margin: 0;
          }
          .mengenstaffelung-popup-close {
            background: none;
            border: none;
            font-size: 25px;
            cursor: pointer;
            padding: 5px;
          }
          .mengenstaffelung-popup-content {
            margin-bottom: 15px;
          }
          .mengenstaffelung-popup-item {
            margin-bottom: 10px;
            padding-bottom: 10px;
            border-bottom: 1px solid #eee;
          }
          .mengenstaffelung-popup-item:last-child {
            border-bottom: none;
          }
          .mengenstaffelung-popup-buttons {
            display: flex;
            justify-content: center;
            gap: 10px;
          }
          .mengenstaffelung-popup-button {
            padding: 8px 16px;
            border-radius: 4px;
            cursor: pointer;
          }
          .mengenstaffelung-popup-button--primary {
            background: var(--color-button);
            color: var(--color-button-text);
            border: none;
          }
          .mengenstaffelung-popup-button--secondary {
            background: transparent;
            border: 1px solid var(--color-button);
            color: var(--color-button);
          }
          .correct-quantity {
            color: #43c86e;
          }
        `;
        document.head.appendChild(style);
      }
    }
    
    // Create overlay
    let overlay = document.getElementById('mengenstaffelung-popup-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'mengenstaffelung-popup-overlay';
      overlay.className = 'mengenstaffelung-popup-overlay';
      document.body.appendChild(overlay);
    }
    
    // Get localized strings with proper fallbacks
    const strings = {};
    try {
      // Check if theme object and necessary properties exist
      if (window.theme && window.theme.strings && 
          window.theme.strings.general && 
          window.theme.strings.general.cart && 
          window.theme.strings.general.cart.mengenstaffelung) {
        Object.assign(strings, window.theme.strings.general.cart.mengenstaffelung);
      }
      console.log('Localization strings:', strings);
    } catch (e) {
      console.warn('Could not access theme strings:', e);
    }
    
    // Build popup content
    let content = `
      <div class="mengenstaffelung-popup-header">
        <h3 class="mengenstaffelung-popup-title">${strings.popup_title || 'Bestellmenge nicht möglich'}</h3>
        <button class="mengenstaffelung-popup-close" aria-label="${strings.close_button_label || 'Schließen'}">&times;</button>
      </div>
      <div class="mengenstaffelung-popup-content">
        <p>${strings.explanation || 'Die folgenden Produkte haben eine Mengenstaffelung, die nicht eingehalten wurde:'}</p>
        <div class="mengenstaffelung-popup-items">
    `;
    
    invalidItems.forEach(item => {
      const currentQuantity = strings.current_quantity ? 
        strings.current_quantity.replace('{{ quantity }}', item.quantity) : 
        `Aktuelle Menge: ${item.quantity}`;
      
      const stepSize = strings.step_size ? 
        strings.step_size.replace('{{ step }}', item.step) : 
        `Mengenstaffelung: ${item.step}`;
      
      const suggestedQuantity = strings.suggested_quantity ? 
        strings.suggested_quantity.replace('{{ quantity }}', item.suggestedQuantity) : 
        `Empfohlene Menge: ${item.suggestedQuantity}`;
      
      content += `
        <div class="mengenstaffelung-popup-item">
          <strong>${item.title}</strong>
          <p>${currentQuantity}</p>
          <p>${stepSize}</p>
          <strong class="correct-quantity">${suggestedQuantity}</strong>
        </div>
      `;
    });
    
    content += `
        </div>
      </div>
      <div class="mengenstaffelung-popup-buttons">
        <button class="btn btn--primary" data-action="close">${strings.back_to_cart || 'Zurück zum Warenkorb'}</button>
      </div>
    `;
    
    popup.innerHTML = content;
    popup.style.display = 'block';
    overlay.style.display = 'block';
    
    // Add event listeners
    const closeButton = popup.querySelector('.mengenstaffelung-popup-close');
    const backButton = popup.querySelector('[data-action="close"]');
    const proceedButton = popup.querySelector('[data-action="proceed"]');
    
    const closePopup = () => {
      popup.style.display = 'none';
      overlay.style.display = 'none';
    };
    
    const proceedToCheckout = () => {
      closePopup();
      // Proceed to checkout
      window.location.href = '/checkout';
    };
    
    // Log that we're using localized strings
    console.log('Using localized strings for mengenstaffelung popup');
    
    closeButton.addEventListener('click', closePopup);
    backButton.addEventListener('click', closePopup);
    proceedButton.addEventListener('click', proceedToCheckout);
    overlay.addEventListener('click', closePopup);
  }
}
